<template>
  <div v-if="user.login_method === 'password' && user.passwordless_status !== 'completed'">
    <div class="flex justify-between items-start w-full">
      <h3 class="mt-6 ml-3 sm:ml-6 mb-6 text-zyxel-gray-900 font-bold">
        {{ $t('messages.account_info.password') }}
      </h3>
      <div class="hidden sm:block flex-col items-end md:flex mt-6 mr-6">
        <div
          v-if="!isEditing"
          class="flex-row block text-right"
        >
          <button class="button--text">
            <button
              role="button"
              class="label text-zyxel-green-700 font-bold cursor-pointer"
              @click="toggleEdit"
              @keydown.enter="toggleEdit"
            >
              {{
                $t('messages.common.link_change_password') }}
            </button>
          </button>
          <div
            v-if="user.last_update_password_at"
            class="text-[12px] leading-[18px] text-gray-500"
          >
            {{ $t('messages.account_info.last_change_on') }} {{ formatDate(user.last_update_password_at) }}
          </div>
        </div>
        <div
          v-else
          class="flex-row block"
        >
          <button
            class="text-zyxel-green-700 font-bold cursor-pointer"
            @click="cancelEdit"
          >
            {{ $t('messages.common.link_cancel') }}
          </button>
          <button
            class="cursor-not-allowed text-zyxel-gray-800 font-bold ml-2"
            :class="{ 'cursor-pointer text-zyxel-green-700': !isDemoAccount }"
            @click="saveEdit"
          >
            {{ $t('messages.common.link_save') }}
          </button>
        </div>
      </div>
    </div>
    <div
      v-if="isEditing"
      class="caption w-full"
    >
      <dl class="grid sm:grid-cols-[auto_1fr] gap-x-4 gap-y-1 sm:gap-y-2">
        <dt class="flex items-center text-zyxel-gray-900 max-w-[240px] ml-3 sm:ml-6 sm:mb-5 h-12">
          {{
            $t('messages.text_input.current_password') }}<span class="ml-2 text-zyxel-red-700">*</span>
        </dt>
        <dd class="px-3 sm:px-0 sm:mr-6">
          <label
            for="current_pwd"
            class="sr-only"
          >{{ $t('messages.text_input.current_password') }}</label>
          <PasswordInput
            :id="'current_pwd'"
            v-model="passwords.current_pwd"
            :class="{ 'error-input': errors.current_pwd }"
          />
          <span
            v-if="errors.current_pwd"
            class="error-message mt-1 b2 block text-left w-full"
          >{{
            $t(errors_msg.password) }}</span>
        </dd>
        <dt class="flex items-center text-zyxel-gray-900 max-w-[240px] ml-3 sm:ml-6 sm:mb-5 h-12">
          {{
            $t('messages.text_input.new_password') }}<span class="ml-2 text-zyxel-red-700">*</span>
        </dt>
        <dd class="px-3 sm:px-0 sm:mr-6">
          <label
            for="new_pwd"
            class="sr-only"
          >{{ $t('messages.text_input.new_password') }}</label>
          <PasswordInput
            :id="'new_pwd'"
            v-model="passwords.new_pwd"
            :class="{ 'error-input': errors.new_pwd }"
          />
          <span
            v-if="errors.new_pwd"
            class="error-message mt-1 b2 block text-left w-full"
          >{{
            $t(errors_msg.password) }}</span>
          <span class="b3 block text-left flex"><img
            class="h-3 mt-1 mr-1"
            src="@/assets/svg/icon_24px_info.svg"
            alt=""
          >{{
            $t('messages.text_input.password_info', { special_words: '!@#$%^&*()_+' }) }}</span>
        </dd>
        <dt class="flex items-center text-zyxel-gray-900 max-w-[240px] ml-3 sm:ml-6 sm:mb-5 h-12">
          {{
            $t('messages.text_input.confirm_password') }}<span class="ml-2 text-zyxel-red-700">*</span>
        </dt>
        <dd class="px-3 sm:px-0 sm:mr-6">
          <label
            for="confirm_pwd"
            class="sr-only"
          >{{ $t('messages.text_input.confirm_password') }}</label>
          <PasswordInput
            :id="'confirm_pwd'"
            v-model="passwords.confirm_pwd"
            :error="errors.confirm_pwd"
          />
          <span
            v-if="errors.confirm_pwd"
            class="error-message mt-1 b2 block text-left w-full"
          >{{
            $t(errors_msg.password) }}</span>
        </dd>
      </dl>
    </div>
    <div class="sm:hidden w-full my-6 text-center items-center">
      <div
        v-if="!isEditing"
        class="flex-row block"
      >
        <button class="button--text">
          <button
            role="button"
            class="label text-zyxel-green-700 font-bold cursor-pointer"
            @click="toggleEdit"
            @keydown.enter="toggleEdit"
          >
            {{
              $t('messages.common.link_change_password') }}
          </button>
        </button>
        <div
          v-if="user.last_update_password_at"
          class="text-[12px] leading-[18px] text-gray-500"
        >
          {{ $t('messages.account_info.last_change_on') }} {{ formatDate(user.last_update_password_at) }}
        </div>
      </div>
      <div
        v-else
        class="flex-row block"
      >
        <button
          class="text-zyxel-green-700 font-bold cursor-pointer"
          @click="cancelEdit"
        >
          {{ $t('messages.common.link_cancel') }}
        </button>
        <button
          class="cursor-not-allowed text-zyxel-gray-800 font-bold ml-2"
          :class="{ 'cursor-pointer text-zyxel-green-700': !isDemoAccount }"
          @click="saveEdit"
        >
          {{ $t('messages.common.link_save') }}
        </button>
      </div>
    </div>

    <!-- Passwordless Recommendation -->
    <div
      v-if="user.passwordless_status === 'eligible' && !isEditing"
      class="px-3 sm:px-6 py-4"
    >
      <div class="flex items-start gap-2">
        <div class="text-gray-700">
          <p class="mb-2">
            {{ $t('messages.passwordless.recommendation') }}
          </p>
          <button
            class="text-zyxel-green-700 font-bold cursor-pointer hover:underline"
            @click="handleGoPasswordless"
          >
            {{ $t('messages.passwordless.go_passwordless') }}
          </button>
        </div>
      </div>
    </div>
  </div>
  <LogoutModal
    :show-modal="showModal"
    @close-modal="showModal = false"
  />
  <PasswordlessConfirmModal
    :is-visible="showPasswordlessModal"
    @close-modal="showPasswordlessModal = false"
    @confirm="confirmEnablePasswordless"
  />
  <GeneralDialog
    :is-show="showSuccessModal"
    :title="$t('messages.passwordless.success_modal.title')"
    :description="$t('messages.passwordless.success_modal.description')"
    @close="showSuccessModal = false"
  />
</template>

<script>
import { useAuthStore } from '@/stores/auth'
import LogoutModal from '@/components/LogoutModal.vue'
import PasswordlessConfirmModal from '@/components/PasswordlessConfirmModal.vue'
import GeneralDialog from '@/components/GeneralDialog.vue'
import PasswordInput from '@/components/PasswordInput.vue'
import { logger } from '@/utils/logger';

export default {
  name: 'PasswordChange',
  components: {
    LogoutModal,
    PasswordlessConfirmModal,
    GeneralDialog,
    PasswordInput
  },
  props: {
    user: Object
  },
  data() {
    return {
      isEditing: false,
      passwords: {
        current_pwd: "",
        new_pwd: "",
        confirm_pwd: ""
      },
      errors: {
        current_pwd: false,
        new_pwd: false,
        confirm_pwd: false
      },
      errors_msg: {
        password: ""
      },
      showModal: false,
      showPasswordlessModal: false,
      showSuccessModal: false
    }
  },
  computed: {
    isDemoAccount() {
      const authStore = useAuthStore();
      return authStore.isDemoAccount;
    }
  },
  methods: {
    toggleEdit() {
      this.isEditing = !this.isEditing;
    },
    cancelEdit() {
      this.clearErrors();
      this.isEditing = false;
    },
    formatDate(utcDateString) {
      const localDate = new Date(utcDateString);
      const year = localDate.getFullYear();
      const month = (localDate.getMonth() + 1).toString().padStart(2, '0');
      const day = localDate.getDate().toString().padStart(2, '0');

      return `${year}-${month}-${day}`;
    },
    async saveEdit() {
      if (this.isDemoAccount) {
        return;
      }

      if (!this.checkInputs()) {
        return;
      }
      const authStore = useAuthStore();

      const pwdData = {
        old_password: this.passwords.current_pwd,
        new_password: this.passwords.new_pwd
      };

      let result = await authStore.changeUserPassword(pwdData);
      if (result) {
        this.isEditing = false;
        this.showModal = true;
      }
      else {
        this.errors.current_pwd = true;
        this.errors_msg.password = "messages.error_msg.password_incorrect";
      }
    },
    checkInputs() {
      this.errors.current_pwd = false;
      this.errors.new_pwd = false;
      this.errors.confirm_pwd = false;

      if (this.passwords.current_pwd.length === 0) {
        this.errors.current_pwd = true;
        this.errors_msg.password = "messages.error_msg.password_empty";
        return false;
      }

      const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d]).{8,}$/;

      logger.log(this.passwords.new_pwd);
      logger.log(passwordRegex.test(this.passwords.new_pwd))
      if (this.passwords.new_pwd.length === 0) {
        this.errors.new_pwd = true;
        this.errors_msg.password = "messages.error_msg.password_empty";
        return false;
      } else {
        if (!passwordRegex.test(this.passwords.new_pwd)) {
          this.errors.new_pwd = true;
          this.errors_msg.password = "messages.error_msg.password_complexity";
          return false;
        } else {
          if (this.passwords.current_pwd === this.passwords.new_pwd) {
            this.errors.new_pwd = true;
            this.errors_msg.password = "messages.error_msg.password_differ";
            return false;
          }
        }
      }

      if (this.passwords.confirm_pwd.length === 0) {
        this.errors.confirm_pwd = true;
        this.errors_msg.password = "messages.error_msg.password_empty";
        return false;
      } else {
        if (this.passwords.new_pwd !== this.passwords.confirm_pwd) {
          this.errors.confirm_pwd = true;
          this.errors_msg.password = "messages.error_msg.password_unmatched";
          return false;
        }
      }

      return true;
    },
    clearErrors() {
      this.errors.current_pwd = false;
      this.errors.new_pwd = false;
      this.errors.confirm_pwd = false;
    },
    handleGoPasswordless() {
      this.showPasswordlessModal = true;
    },
    async confirmEnablePasswordless() {
      const authStore = useAuthStore();

      try {
        const result = await authStore.enablePasswordless();

        if (result.success) {
          this.showPasswordlessModal = false;
          this.showSuccessModal = true;
          // User data will be automatically refreshed by authStore.enablePasswordless()
        } else {
          this.showPasswordlessModal = false;
          // Show error notification
          alert(this.$t('messages.passwordless.error_message'));
          logger.error('Failed to enable passwordless:', result.error);
        }
      } catch (error) {
        this.showPasswordlessModal = false;
        alert(this.$t('messages.passwordless.error_message'));
        logger.error('Error enabling passwordless:', error);
      }
    }
  }
}
</script>